import type { WorkflowManifest, StepConfig } from "../../types/workflow-schema";
import cachedManifest from "./default-manifest.json";
import cachedStepConfigs from "./default-step-configs.json";

/**
 * Build-time cached workflow config.
 * These JSON files are bundled into the production build and serve as fallbacks
 * when Sanity CMS is unreachable.
 *
 * In production, these are replaced by the Vite plugin that fetches the latest
 * config from Sanity at build time. For local dev, these defaults mirror
 * the booking-v3 step sequences.
 */

export const getCachedManifest = (): WorkflowManifest => {
  return cachedManifest as WorkflowManifest;
};

/**
 * Merges fields from component content blocks into the step's top-level fields[].
 * Allows component blocks (service-selector, availability-form) to own their field
 * definitions while still exposing them via stepConfig.fields for backward compat.
 */
export const mergeComponentBlockFields = (config: StepConfig): StepConfig => {
  const componentBlockFields = (config.contentBlocks ?? [])
    .filter(
      (b): b is import("../../types/workflow-schema").ComponentContentBlock =>
        b.blockType === "component" &&
        Array.isArray((b as any).fields) &&
        (b as any).fields.length > 0,
    )
    .flatMap((b) => (b as any).fields as StepConfig["fields"]);

  if (!componentBlockFields.length) return config;
  return {
    ...config,
    fields: [...(config.fields ?? []), ...componentBlockFields],
  };
};

export const getCachedStepConfig = (
  stepId: string,
  categorySlug?: string | null,
): StepConfig | null => {
  const configs = cachedStepConfigs as Record<string, StepConfig>;
  // Try category-specific key first (e.g. "Labor::contact-information"),
  // then fall back to the shared key (e.g. "contact-information").
  const config =
    (categorySlug && configs[`${categorySlug}::${stepId}`]) || configs[stepId];
  return config ? mergeComponentBlockFields(config) : null;
};
